import { useEffect, useState } from "react"
import { Head, Link, router, usePoll } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner, ProgressBar } from "@/components/ui/progress"
import { PromoteSheetDialog } from "@/components/plan-sets/PromoteSheetDialog"
import {
  PROCESSING_LABELS,
  PROCESSING_TONES,
  renderTarget,
  renderedOfTarget,
  type PlanSetRow,
  type SheetRow,
} from "@/lib/plan-set-status"

type PageEntry = {
  number: number
  rendered: boolean
  thumb_url: string | null
  preview_url: string | null
  sheet: SheetRow | null
}

type Props = {
  owner: { kind: "project" | "community"; id: number; display_name: string; url: string }
  sheets_url: string
  plan_set: PlanSetRow
  composite_sheets: { id: number; label: string }[]
  plans: { id: number; display_name: string }[]
  pages: PageEntry[]
}

type DialogTarget = { page: PageEntry; existing?: { id: number; label: string; rotation: number } }

// A requested page whose thumbnail hasn't appeared after this long has
// almost certainly failed server-side (the job re-raises into Solid Queue /
// Sentry rather than flipping the plan set to failed) — put the button back.
const PENDING_TIMEOUT_MS = 120_000

export default function PlanSetsShow({ owner, sheets_url, plan_set: planSet, composite_sheets, plans, pages }: Props) {
  const [dialogTarget, setDialogTarget] = useState<DialogTarget | null>(null)
  // page number -> when its render was requested (client-side only; refresh
  // forgets it and the card just shows its Render button again).
  const [requestedAt, setRequestedAt] = useState<Map<number, number>>(new Map())

  const pendingPages = new Set(
    [...requestedAt.entries()]
      .filter(([page, at]) => {
        const entry = pages[page - 1]
        return entry && !entry.rendered && Date.now() - at < PENDING_TIMEOUT_MS
      })
      .map(([page]) => page),
  )

  const active =
    planSet.status === "queued" ||
    planSet.status === "processing" ||
    pendingPages.size > 0 ||
    planSet.sheets.some((s) => s.status === "queued" || s.status === "processing")
  const { start, stop } = usePoll(2000, { only: ["plan_set", "pages"] }, { autoStart: false })
  useEffect(() => {
    if (active) start()
    else stop()
  }, [active, start, stop])

  // Drop bookkeeping for pages that have landed.
  useEffect(() => {
    setRequestedAt((prev) => {
      const next = new Map([...prev].filter(([page]) => !pages[page - 1]?.rendered))
      return next.size === prev.size ? prev : next
    })
  }, [pages])

  const requestRender = (pageNumbers: number[] | "all") => {
    const now = Date.now()
    const targets =
      pageNumbers === "all"
        ? pages.filter((p) => !p.rendered).map((p) => p.number)
        : pageNumbers
    setRequestedAt((prev) => {
      const next = new Map(prev)
      targets.forEach((page) => next.set(page, now))
      return next
    })
    router.post(
      planSet.render_pages_url,
      { pages: pageNumbers === "all" ? "all" : pageNumbers },
      { preserveScroll: true, preserveState: true },
    )
  }

  const unrendered = planSet.page_count ? planSet.page_count - planSet.rendered_pages.length : 0
  const description = `Promote the pages worth measuring — ${planSet.page_count ?? "…"} pages in this set.`

  return (
    <>
      <Head title={planSet.title}>
        <meta name="description" content={`Pick and label the measurable pages of ${planSet.title}.`} />
        <meta property="og:title" content={planSet.title} />
        <meta property="og:description" content={`Pick and label the measurable pages of ${planSet.title}.`} />
      </Head>
      <AppShell wide>
        <PageHeader
          title={
            <span className="flex items-center gap-3">
              {planSet.title}
              <Badge tone={PROCESSING_TONES[planSet.status]}>{PROCESSING_LABELS[planSet.status]}</Badge>
            </span>
          }
          description={
            <>
              <Link href={owner.url}>← {owner.display_name}</Link>
              {" · "}
              {description}
            </>
          }
          actions={
            <span className="flex items-center gap-2">
              {planSet.status === "ready" && unrendered > 0 && (
                <Button variant="ghost" onClick={() => requestRender("all")}>
                  Render all {unrendered} remaining
                </Button>
              )}
              <Button asChild variant="ghost">
                <a href={planSet.download_url}>Download PDF</a>
              </Button>
            </span>
          }
        />

        {(planSet.status === "queued" || planSet.status === "processing") && (
          <div className="mt-6 flex max-w-sm items-center gap-3">
            <Spinner />
            <div className="flex-1 space-y-1">
              <ProgressBar
                value={renderTarget(planSet) ? (renderedOfTarget(planSet) / renderTarget(planSet)) * 100 : 0}
              />
              <p className="text-xs text-ink-muted">
                {planSet.page_count
                  ? `${renderedOfTarget(planSet)} / ${renderTarget(planSet)} pages processed`
                  : "Reading the PDF…"}
              </p>
            </div>
          </div>
        )}
        {planSet.status === "failed" && (
          <div className="callout callout-danger mt-6">
            <p>{planSet.error_message || "Processing failed."}</p>
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {pages.map((page) => (
            <PageCard
              key={page.number}
              page={page}
              planSetStatus={planSet.status}
              pending={pendingPages.has(page.number)}
              onRequestRender={() => requestRender([page.number])}
              onPromote={() => setDialogTarget({ page })}
              onEdit={(sheet) =>
                setDialogTarget({
                  page,
                  existing: { id: sheet.id, label: sheet.label, rotation: sheet.rotation },
                })
              }
            />
          ))}
        </div>

        {dialogTarget && (
          <PromoteSheetDialog
            open
            onOpenChange={(open) => {
              if (!open) setDialogTarget(null)
            }}
            sheetsUrl={sheets_url}
            planSetId={planSet.id}
            pageNumber={dialogTarget.page.number}
            thumbUrl={dialogTarget.page.thumb_url}
            previewUrl={dialogTarget.page.preview_url}
            existing={dialogTarget.existing}
            compositeSheets={composite_sheets}
            plans={plans}
          />
        )}
      </AppShell>
    </>
  )
}

function PageCard({
  page,
  planSetStatus,
  pending,
  onRequestRender,
  onPromote,
  onEdit,
}: {
  page: PageEntry
  planSetStatus: PlanSetRow["status"]
  pending: boolean
  onRequestRender: () => void
  onPromote: () => void
  onEdit: (sheet: SheetRow) => void
}) {
  const sheet = page.sheet
  // While the initial upload is processing, unrendered cards may still be in
  // the upload's own selection — show a quiet placeholder, not a button that
  // would double-request them.
  const processing = planSetStatus === "queued" || planSetStatus === "processing"
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-hairline bg-page">
      <div className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-surface">
        {page.rendered && page.thumb_url ? (
          <img
            src={page.thumb_url}
            alt={`Page ${page.number}`}
            loading="lazy"
            className="max-h-full max-w-full object-contain"
          />
        ) : pending || processing ? (
          <Spinner size="md" label={`Rendering page ${page.number}`} />
        ) : (
          <div className="flex flex-col items-center gap-2 text-ink-muted">
            <span className="text-2xl font-medium">{page.number}</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={onRequestRender}
              data-testid={`render-page-${page.number}`}
            >
              Render preview
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-2 border-t border-hairline px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium uppercase tracking-wider text-ink-muted">
            Page {page.number}
          </span>
          {sheet && (
            <Badge tone={sheet.status === "failed" ? "signal" : "accent"}>{sheet.label}</Badge>
          )}
        </div>
        {sheet ? (
          <div className="flex flex-wrap items-center gap-2">
            {sheet.status === "ready" && (
              <Button asChild size="sm">
                <Link href={sheet.editor_url}>Open in editor</Link>
              </Button>
            )}
            {(sheet.status === "queued" || sheet.status === "processing") && (
              <span className="flex items-center gap-2 text-xs text-ink-muted">
                <Spinner /> Tiling…
              </span>
            )}
            {sheet.status === "failed" && (
              <span className="text-xs text-danger-display">{sheet.error_message || "Tiling failed"}</span>
            )}
            <Button variant="ghost" size="sm" onClick={() => onEdit(sheet)}>
              Edit
            </Button>
          </div>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={onPromote}
            disabled={!page.thumb_url}
          >
            Promote
          </Button>
        )}
      </div>
    </div>
  )
}
